// Menu Administração > 01 Identidade Visual. Backend próprio (GPS Drive
// Backend, ver src/admin-branding/ no worktree de release) — GET é aberto
// a qualquer usuário logado da empresa (aplicado no app inteiro via
// use-branding-theme.ts), PUT/uploads exigem a permissão
// administracao.identidade_visual (ver gpsdrive-user-permissions.service.ts).
import { gpsDriveGet, gpsDrivePut, gpsDriveUpload } from "@/lib/api/gpsdrive-client";
import { compressImageFile } from "@/lib/compress-image";

// Fundos da Tela de Login baixam em TODA visita antes do login ficar
// completo — um PNG de 2 MB aqui deixava clientes em 4G olhando tela
// escura (2026-09-24). Comprime antes de subir.
const uploadLoginBackground = async (path: string, file: File) =>
  gpsDriveUpload<UploadBrandingImageResult>(path, await compressImageFile(file, { maxDimension: 2560 }));

export interface AdminBrandingSettings {
  themeMode: "light" | "dark";
  primaryColor: string | null;
  loginBackgroundColor: string | null;
  loginTextColor: string | null;
  loginBackgroundTheme: string;
  logoSize: number | null;
  loginLogoSize: number | null;
  logoUrl: string | null;
  // Logotipo do topo para o tema Escuro — null usa o logoUrl normal.
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  loginLogoUrl: string | null;
  loginBackgroundImageUrl: string | null;
  loginBackgroundTabletImageUrl: string | null;
  loginBackgroundPwaImageUrl: string | null;
  serverName: string | null;
  serverDescription: string | null;
  requireClientContractSignature: boolean;
  appleStoreUrl: string | null;
  googlePlayUrl: string | null;
  appManualUrl: string | null;
  termsOfUseUrl: string | null;
  privacyPolicyUrl: string | null;
  instagramUrl: string | null;
  facebookUrl: string | null;
  twitterUrl: string | null;
  emergencyPhone: string | null;
  appEnabled: boolean;
  appPlatformAndroid: boolean;
  appPlatformIos: boolean;
  appPrimaryColor: string | null;
  appIconSize: number | null;
  appName: string | null;
  appDomain: string | null;
  appShowNameOnMap: boolean;
  // Notificação "nova atualização disponível" (Início/PWA e desktop) — liga
  // por padrão; admin desliga aqui pra não mostrar esse aviso aos clientes
  // da empresa. Ver src/components/update-available-notice.tsx.
  updateNotificationEnabled: boolean;
}

// Campo omitido mantém o valor já salvo; `null` explícito limpa o campo —
// cada tela de Identidade Visual manda só a fatia que edita.
export type UpdateAdminBrandingInput = Partial<
  Omit<AdminBrandingSettings, "logoUrl" | "logoDarkUrl" | "faviconUrl" | "loginLogoUrl" | "loginBackgroundImageUrl" | "loginBackgroundTabletImageUrl" | "loginBackgroundPwaImageUrl">
>;

export interface UploadBrandingImageResult {
  key: string;
  url: string;
}

// Fatia pública (sem autenticação) consumida pela Tela de Login antes do
// login — ver PublicLoginBrandingResponse no backend.
export interface PublicLoginBrandingSettings {
  loginBackgroundTheme: string;
  loginBackgroundColor: string | null;
  loginTextColor: string | null;
  loginBackgroundImageUrl: string | null;
  loginBackgroundTabletImageUrl: string | null;
  loginBackgroundPwaImageUrl: string | null;
  loginLogoUrl: string | null;
  loginLogoSize: number | null;
}

export const gpsDriveAdminBrandingService = {
  get: () => gpsDriveGet<AdminBrandingSettings>("/api/v1/admin/branding"),
  getPublicLoginBranding: () =>
    gpsDriveGet<PublicLoginBrandingSettings>("/api/v1/public/login-branding"),
  update: (input: UpdateAdminBrandingInput) =>
    gpsDrivePut<AdminBrandingSettings>("/api/v1/admin/branding", input),
  uploadLogo: (file: File) =>
    gpsDriveUpload<UploadBrandingImageResult>("/api/v1/admin/branding/logo", file),
  uploadLogoDark: (file: File) =>
    gpsDriveUpload<UploadBrandingImageResult>("/api/v1/admin/branding/logo-dark", file),
  uploadFavicon: (file: File) =>
    gpsDriveUpload<UploadBrandingImageResult>("/api/v1/admin/branding/favicon", file),
  uploadLoginLogo: (file: File) =>
    gpsDriveUpload<UploadBrandingImageResult>("/api/v1/admin/branding/login-logo", file),
  uploadLoginBackgroundImage: (file: File) =>
    uploadLoginBackground("/api/v1/admin/branding/login-background", file),
  uploadLoginBackgroundTabletImage: (file: File) =>
    uploadLoginBackground("/api/v1/admin/branding/login-background-tablet", file),
  uploadLoginBackgroundPwaImage: (file: File) =>
    uploadLoginBackground("/api/v1/admin/branding/login-background-pwa", file),
};
